import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Loader2 } from "lucide-react";
import ApiClient from "../utils/api";
import { BrandMark } from "../components/ChannelLogo";
import { useToast } from "../context/ToastContext";

const integrationCards = [
  {
    id: "freshchat",
    title: "Freshchat",
    description: "Omni-channel customer support & live chat",
    icon: <img src="/freshchat.png" alt="Freshchat" className="w-9 h-9 object-contain rounded-md" />,
    route: "/freshchat-config",
    category: "Support",
  },
  {
    id: "freshdesk",
    title: "Freshdesk",
    description: "Customer service software & support ticket system",
    icon: <img src="/freshdesk.png" alt="Freshdesk" className="w-9 h-9 object-contain rounded-md" />,
    route: "/freshdesk-config",
    category: "Helpdesk",
  },
  {
    id: "telegram",
    title: "Telegram",
    description: "High-speed bot integration for instant messaging",
    icon: <BrandMark channel="telegram" className="w-9 h-9" />,
    route: "/telegram-config",
    category: "Messaging",
  },
  {
    id: "slack",
    title: "Slack",
    description: "Collaborative AI responses for your workspaces",
    icon: <img src="/slack.svg" alt="Slack" className="w-9 h-9 object-contain rounded-md" />,
    route: "/slack-config",
    category: "Messaging",
  },
  {
    id: "whatsapp",
    title: "WhatsApp",
    description: "Engage users on the world's most popular messaging app",
    icon: <BrandMark channel="whatsapp" className="w-9 h-9" />,
    route: "/whatsapp-config",
    category: "Messaging",
  },
  {
    id: "instagram",
    title: "Instagram",
    description: "Answer Instagram direct messages with your AI agent",
    icon: <BrandMark channel="instagram" className="w-9 h-9" />,
    route: "/instagram-config",
    category: "Messaging",
  },
  {
    id: "instagram-own",
    title: "Instagram (own Meta app)",
    description: "Instagram DMs through your own Meta developer app",
    icon: <BrandMark channel="instagram" className="w-9 h-9" />,
    route: "/instagram-own-config",
    category: "Messaging",
  },
  {
    id: "shopify",
    title: "Shopify",
    description: "E-commerce order tracking, lookup & catalog integration",
    icon: <img src="/shopify.png" alt="Shopify" className="w-9 h-9 object-contain rounded-md" />,
    route: "/shopify-config",
    category: "E-commerce",
  },
  {
    id: "email",
    title: "Email",
    description: "Automate inbound email tickets & support replies",
    icon: <img src="/gmail.png" alt="Email" className="w-9 h-9 object-contain rounded-md" />,
    route: "/email-config",
    category: "Support",
  },
  {
    id: "zendesk",
    title: "Zendesk",
    description: "Customer service platform & ticket management",
    icon: <img src="/zendesk.png" alt="Zendesk" className="w-9 h-9 object-contain rounded-md" />,
    route: "/zendesk-config",
    category: "Helpdesk",
  },
];

const Integrations = ({ activeAiAgentId }) => {
  const navigate = useNavigate();
  const { showSuccessToast, showErrorToast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statuses, setStatuses] = useState({
    freshchat: false,
    freshdesk: false,
    telegram: false,
    slack: false,
    whatsapp: false,
    instagram: false,
    "instagram-own": false,
    email: false,
    zendesk: false,
    shopify: false,
  });
  const [configs, setConfigs] = useState({});
  const [togglingId, setTogglingId] = useState(null);

  const checkStatuses = useCallback(async () => {
    const agentId = activeAiAgentId || localStorage.getItem("activeAiAgentId");
    const options = { aiAgentId: agentId };

    const checks = [
      {
        key: "freshchat",
        req: ApiClient.request("/freshchat/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.freshchat_url || res?.config?.api_token || res?.config?.app_id);
          return {
            active: Boolean(res?.success && res?.config?.is_active),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "freshdesk",
        req: ApiClient.request("/freshdesk/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.freshdesk_domain || res?.config?.api_key);
          return {
            active: Boolean(res?.success && res?.config?.is_active),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "telegram",
        req: ApiClient.request("/telegram/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.bot_token);
          return {
            active: Boolean(res?.success && res?.config?.is_active),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "slack",
        req: ApiClient.request("/slack/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.bot_token || res?.config?.signing_secret);
          return {
            active: Boolean(res?.success && res?.config?.is_active),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "whatsapp",
        req: ApiClient.request("/whatsapp/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.access_token || res?.config?.phone_number_id);
          return {
            active: Boolean(res?.success && res?.config?.is_active),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "instagram",
        req: ApiClient.request("/instagram/config", options).then((res) => {
          // one Instagram setup per bot: Kambaa's app here, the own-app setup on its own card
          const own = res?.config?.setup === "manual";
          const hasCreds = Boolean(res?.config?.ig_user_id) && !own;
          return {
            active: Boolean(res?.success && res?.config?.is_active) && !own,
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "instagram-own",
        req: ApiClient.request("/instagram/config", options).then((res) => {
          const own = res?.config?.setup === "manual";
          return {
            active: Boolean(res?.success && res?.config?.is_active) && own,
            configured: Boolean(res?.config?.ig_user_id) && own,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "email",
        req: ApiClient.request("/email/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.google_connected || res?.config?.smtp_host || res?.config?.imap_host);
          return {
            active: Boolean(res?.success && (res?.config?.is_active || res?.config?.google_connected)),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "zendesk",
        req: ApiClient.request("/zendesk/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.subdomain || res?.config?.api_token);
          return {
            active: Boolean(res?.success && res?.config?.is_active),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
      {
        key: "shopify",
        req: ApiClient.request("/shopify/config", options).then((res) => {
          const hasCreds = Boolean(res?.config?.shop_url || res?.config?.access_token);
          return {
            active: Boolean(res?.success && res?.config?.is_active),
            configured: hasCreds,
            config: res?.config || {},
          };
        }),
      },
    ];

    const results = await Promise.allSettled(
      checks.map(async (c) => {
        try {
          const data = await c.req;
          return { key: c.key, ...data };
        } catch {
          return { key: c.key, active: false, configured: false, config: {} };
        }
      })
    );

    const updatedStatuses = {};
    const updatedConfigs = {};

    results.forEach((r) => {
      if (r.status === "fulfilled" && r.value) {
        updatedStatuses[r.value.key] = Boolean(r.value.active);
        updatedConfigs[r.value.key] = {
          configured: Boolean(r.value.configured),
          data: r.value.config,
        };
      }
    });

    setStatuses((prev) => ({ ...prev, ...updatedStatuses }));
    setConfigs(updatedConfigs);
  }, [activeAiAgentId]);

  useEffect(() => {
    checkStatuses();
  }, [checkStatuses]);

  const handleToggle = async (card, e) => {
    e.stopPropagation();
    const isCurrentActive = Boolean(statuses[card.id]);
    const nextState = !isCurrentActive;
    const cardConfig = configs[card.id];

    if (nextState && (!cardConfig || !cardConfig.configured)) {
      showErrorToast(`Please configure ${card.title} credentials before activating.`);
      navigate(card.route);
      return;
    }

    setTogglingId(card.id);
    const agentId = activeAiAgentId || localStorage.getItem("activeAiAgentId");

    try {
      let endpoint = card.id === "instagram-own" ? "/instagram/manual-config" : `/${card.id}/config`;
      let bodyData = { is_active: nextState };

      // Ensure required preserved fields where backend validation requires them
      if (card.id === "freshdesk" && cardConfig?.data) {
        bodyData = {
          ...cardConfig.data,
          is_active: nextState,
        };
      }

      const res = await ApiClient.request(endpoint, {
        method: "PUT",
        body: JSON.stringify(bodyData),
        aiAgentId: agentId,
      });

      if (res?.success || res?.status === "success" || res?.is_active !== undefined) {
        setStatuses((prev) => ({ ...prev, [card.id]: nextState }));
        showSuccessToast(`${card.title} integration ${nextState ? "activated" : "deactivated"}`);
      } else {
        showErrorToast(res?.message || `Failed to update ${card.title} status`);
      }
    } catch (err) {
      showErrorToast(err?.message || `Failed to update ${card.title}`);
    } finally {
      setTogglingId(null);
    }
  };

  const filteredCards = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return integrationCards;
    return integrationCards.filter(
      (card) =>
        card.title.toLowerCase().includes(query) ||
        card.description.toLowerCase().includes(query) ||
        (card.category && card.category.toLowerCase().includes(query))
    );
  }, [searchQuery]);

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
        {/* Header - Styled like EasyVoice */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Integrations</h1>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">Connect your helpdesk, CRM, or messaging channels.</p>
          </div>
          <div className="text-[11px] sm:text-xs text-zinc-400 font-medium self-start sm:self-auto">
            {integrationCards.length} integrations available
          </div>
        </div>

        {/* Search Bar */}
        <div className="pt-4 sm:pt-5 pb-3">
          <div className="relative w-full sm:max-w-md">
            <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-zinc-400">
              <Search size={14} strokeWidth={2.5} />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search integrations..."
              className="w-full text-xs border border-zinc-200 rounded-lg pl-9 pr-3 py-2.5 focus:border-zinc-900 focus:outline-none bg-white transition-colors text-zinc-800 font-medium placeholder-zinc-400 shadow-sm"
            />
          </div>
        </div>

        {/* Cards Grid */}
        <div className="py-2 sm:py-4">
          {filteredCards.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center max-w-md mx-auto">
              <h3 className="text-sm font-semibold text-zinc-900">No integrations found</h3>
              <p className="text-xs text-zinc-500 mt-1">
                No matching integration for "{searchQuery}". Clear your search query to see all options.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-5">
              {filteredCards.map((card) => {
                const isConnected = Boolean(statuses[card.id]);
                const isConfigured = Boolean(configs[card.id]?.configured);
                const isToggling = togglingId === card.id;

                return (
                  <div
                    key={card.id}
                    onClick={() => navigate(card.route)}
                    className="group bg-white border border-zinc-200 hover:border-zinc-350 rounded-xl p-4 sm:p-5 transition-all duration-200 hover:shadow-sm flex flex-col justify-between cursor-pointer active:scale-[0.99]"
                  >
                    <div>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center shrink-0 transition-transform group-hover:scale-105 duration-200">
                          {card.icon}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-medium text-zinc-500 bg-zinc-100/80 px-2 py-0.5 rounded-md">
                            {card.category}
                          </span>
                          {/* Toggle Switch */}
                          <button
                            type="button"
                            onClick={(e) => handleToggle(card, e)}
                            disabled={isToggling}
                            title={
                              !isConfigured
                                ? "Configure credentials to activate"
                                : isConnected
                                ? "Deactivate integration"
                                : "Activate integration"
                            }
                            className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 cursor-pointer focus:outline-none ${
                              isConnected ? "bg-zinc-900" : "bg-zinc-200 hover:bg-zinc-300"
                            } ${isToggling ? "opacity-60 cursor-wait" : ""}`}
                          >
                            {isToggling ? (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Loader2 size={10} className="animate-spin text-zinc-400" />
                              </div>
                            ) : (
                              <div
                                className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow-xs ${
                                  isConnected ? "left-[18px]" : "left-0.5"
                                }`}
                              />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 sm:mt-3.5">
                        <h3 className="text-sm font-bold text-zinc-900 group-hover:text-black transition-colors">
                          {card.title}
                        </h3>
                        <p className="text-xs text-zinc-500 mt-1 sm:mt-1.5 leading-relaxed line-clamp-2">
                          {card.description}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between border-t border-zinc-100 mt-4 sm:mt-5 pt-3 sm:pt-3.5">
                      {isConnected ? (
                        <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                          Active
                        </span>
                      ) : isConfigured ? (
                        <span className="text-[10px] font-medium text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-zinc-400"></span>
                          Inactive
                        </span>
                      ) : (
                        <span className="text-[10px] font-medium text-zinc-400 uppercase tracking-wider flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-zinc-300"></span>
                          Ready to connect
                        </span>
                      )}

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(card.route);
                        }}
                        className="px-3 py-1.5 bg-zinc-50 hover:bg-zinc-900 text-zinc-700 hover:text-white rounded-lg text-xs font-semibold transition-all duration-150 flex items-center gap-1.5 group-hover:bg-zinc-900 group-hover:text-white"
                      >
                        <span>Configure</span>
                        <ArrowRight size={12} strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Integrations;
